import { parseDateFormat } from "@frontend/components/InsuranceStepper/InsuranceStepper.helper";
import type { State } from "@frontend/lib/graphql/generated-gql";
import { differenceInYears } from "date-fns";
import { z } from "zod";
import type { StateCreator } from "zustand";
import type { Store } from "../globalStore";

export type EligibilityCheckResultCode =
  | "under_aged"
  | "state_not_available"
  | "minor_consent_referral"
  | "provider_oon"
  // | "suicidal_thoughts"
  // | "has_medicaid"
  | "other";

export type storefrontPaymentMode = "CASH" | "INSURED";
export type EligibilityCheckResultType = "passed" | "failed" | "not_checked";
export type EligibilityCheckResult = {
  result: EligibilityCheckResultType;
  code: EligibilityCheckResultCode | null;
  confirmation: boolean;
  /**
   * The state the consumer provided during the eligibility check.
   * For `state_not_available` failures: shown in the "Not available in
   * {state}" banner.
   * For `passed` results: preserved so we can skip re-asking on reopen.
   * Kept on the result (not on the `consumer` slice) so we don't leak
   * partial identity into the persisted store.
   */
  state?: string;
  /**
   * The birthday the consumer provided during the eligibility check.
   * For `passed` results: preserved so we can skip re-asking on reopen
   * and pre-fill the birthday field later in the insurance form.
   */
  birthday?: string;
};

const eligibilityParams = z.object({
  dateOfBirth: z.string().optional(),
  state: z.string().optional(),
  // suicidalThoughts: z.boolean().optional(),
  // isMedicaid: z.boolean().optional(),
});

export interface StorefrontSlice {
  storefrontOrgId: number | null;
  storefrontPaymentMode: storefrontPaymentMode;
  eligibilityCheckResult: EligibilityCheckResult;
  minimumAge: number;
  allowedStates: State[];
  isPendingPricesMode: boolean;
  proceedWithUnverifiedInsurance: boolean;
  postPurchaseRedirectUrl: string | null;
  setStorefrontOrgId: (orgId: number) => void;
  setStorefrontPaymentMode: (arg: storefrontPaymentMode) => void;
  setEligibilityCheckResult: (arg: EligibilityCheckResult) => void;
  setMinimumAge: (age: number) => void;
  setAllowedStates: (states: State[]) => void;
  setIsPendingPricesMode: (arg: boolean) => void;
  setProceedWithUnverifiedInsurance: (arg: boolean) => void;
  setPostPurchaseRedirectUrl: (url: string | null) => void;
  checkEligibility: (
    arg: z.infer<typeof eligibilityParams>,
  ) => EligibilityCheckResult | undefined;
}

export type SetterPrefix<T> = T extends `set${string}` ? never : T;
export type StorefrontState = {
  [K in keyof StorefrontSlice as SetterPrefix<K>]: StorefrontSlice[K];
};

export const storefrontInitialState: StorefrontState = {
  storefrontOrgId: null,
  storefrontPaymentMode: "CASH",
  eligibilityCheckResult: {
    result: "not_checked",
    code: null,
    confirmation: false,
  },
  minimumAge: 0,
  allowedStates: [],
  isPendingPricesMode: false,
  proceedWithUnverifiedInsurance: false,
  postPurchaseRedirectUrl: null,
  checkEligibility: () => undefined,
};

const calculateAge = (dob: string) => {
  const today = new Date();
  const birthDate = new Date(dob);
  return differenceInYears(today, birthDate);
};

export const createStorefrontSlice: StateCreator<
  Store,
  [["zustand/devtools", never], ["zustand/persist", unknown]],
  [],
  StorefrontSlice
> = (set, get) => ({
  ...storefrontInitialState,
  setStorefrontOrgId: (orgId: number) => {
    set({ storefrontOrgId: orgId });
  },
  setStorefrontPaymentMode: (arg: storefrontPaymentMode) => {
    set({ storefrontPaymentMode: arg });
  },
  setEligibilityCheckResult: (arg: EligibilityCheckResult) => {
    set({ eligibilityCheckResult: arg });
  },
  setMinimumAge: (age: number) => {
    set({ minimumAge: age });
  },
  setAllowedStates: (states: State[]) => {
    set({ allowedStates: states });
  },
  setIsPendingPricesMode: (arg: boolean) => {
    // Guard: only allow setting to true if proceedWithUnverifiedInsurance flag is enabled
    if (arg && !get().proceedWithUnverifiedInsurance) {
      return;
    }
    set({ isPendingPricesMode: arg });
  },
  setProceedWithUnverifiedInsurance: (arg: boolean) => {
    // When the org disables proceed-with-unverified-insurance, also clear any
    // lingering pending-prices state. Without this, a previously persisted
    // `isPendingPricesMode: true` (from a session when the flag was ON) would
    // survive a reload and force every downstream surface — PaymentForm,
    // PriceDisplay, OrderSummary, ExpandedCartItemsContainer — into the
    // "Pending" UI that displays $0. The org-level decision is "no pending
    // prices at all", so unverified insurance must always fall through to
    // self pay instead.
    if (!arg) {
      set({ proceedWithUnverifiedInsurance: arg, isPendingPricesMode: false });
      return;
    }
    set({ proceedWithUnverifiedInsurance: arg });
  },
  setPostPurchaseRedirectUrl: (url: string | null) => {
    set({ postPurchaseRedirectUrl: url });
  },
  checkEligibility: (params: z.infer<typeof eligibilityParams>) => {
    const { minimumAge, allowedStates } = get();
    if (params.dateOfBirth) {
      const formattedDate = parseDateFormat(params.dateOfBirth);
      const age = calculateAge(formattedDate);
      const consumer = get().consumer;
      if (consumer) {
        set({
          consumer: { ...consumer, dob: formattedDate },
        });
      }
      if (age < minimumAge) {
        const eligibilityCheckResult = {
          result: "failed",
          code: "under_aged",
          confirmation: false,
        } as EligibilityCheckResult;
        set({ eligibilityCheckResult });
        return eligibilityCheckResult;
      }
    }
    if (params.state) {
      const state = params.state.toUpperCase();
      if (allowedStates.length > 0 && !allowedStates.includes(state as State)) {
        const eligibilityCheckResult = {
          result: "failed",
          code: "state_not_available",
          confirmation: false,
          state,
        } as EligibilityCheckResult;
        set({ eligibilityCheckResult });
        return eligibilityCheckResult;
      }
    }
    // if (params.suicidalThoughts) {
    //   const eligibilityCheckResult = {
    //     result: "failed",
    //     code: "suicidal_thoughts",
    //     confirmation: false,
    //   } as EligibilityCheckResult;
    //   set({ eligibilityCheckResult });
    //   return eligibilityCheckResult;
    // }
    // if (params.isMedicaid) {
    //   if (params.isMedicaid) {
    //     const eligibilityCheckResult = {
    //       result: "failed",
    //       code: "has_medicaid",
    //       confirmation: false,
    //     } as EligibilityCheckResult;
    //     set({ eligibilityCheckResult });
    //     return eligibilityCheckResult;
    //   }
    // }

    if (
      // !params.isMedicaid &&
      params.state &&
      // !params.suicidalThoughts &&
      params.dateOfBirth
    ) {
      const consumer = get().consumer;
      if (consumer) {
        set({
          consumer: { ...consumer, state: params.state.toUpperCase() },
        });
      }
      const eligibilityCheckResult = {
        result: "passed",
        code: null,
        confirmation: false,
      } as EligibilityCheckResult;
      set({ eligibilityCheckResult });
      return eligibilityCheckResult;
    }
  },
});
